import React, { useEffect, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getProviders, getProvider } from "api/Providers";
import { getDepotMinimums, setDepotMinimum, type DepotMinimumRow } from "api/InstantEstimate";

/**
 * Company Settings > Instant Estimate: provider / depot minimums. A
 * provider's minimum applies to the service options that provider supplies
 * (providers are usually install-only or delivery-only). Below it, another
 * provider takes that option if one can; otherwise the client sees the
 * option flagged with "Use N yd". A depot row beats its provider's row.
 * Instant Estimate only - staff quotes are unrestricted. Self-saving.
 */
const cell: React.CSSProperties = { fontFamily: "Poppins", fontSize: 13, padding: "7px 6px", borderBottom: "1px solid #F1EFE8" };
const input: React.CSSProperties = { border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 };

const DepotMinimumsSetting: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<DepotMinimumRow[]>([]);
  const [providers, setProviders] = useState<Array<{ id: string; name: string }>>([]);
  const [depots, setDepots] = useState<Array<{ id: string; name: string }>>([]);
  const [draft, setDraft] = useState<{ providerId: string; depotId: string; min: string }>({ providerId: "", depotId: "", min: "" });
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    getDepotMinimums().then((r: any) => { if (!r?.error) setRows(r); });
    getProviders({ query: { pageNum: 1, recordsPer: 500, sortBy: "name", sortDir: "asc" }, excludeInterceptor: true }).then((r: any) => {
      const items = Array.isArray(r?.items) ? r.items : Array.isArray(r) ? r : [];
      setProviders(items.map((p: any) => ({ id: p.id, name: p.name })));
    });
  }, []);

  // Depots of the chosen provider (if the provider record carries them).
  useEffect(() => {
    setDepots([]);
    if (!draft.providerId) return;
    getProvider({ data: { id: draft.providerId } }).then((p: any) => {
      const list = Array.isArray(p?.depots) ? p.depots : [];
      setDepots(list.map((d: any) => ({ id: d.id, name: d.name })));
    });
  }, [draft.providerId]);

  const save = async (providerId: string, depotId: string | null, min: string, key: string) => {
    setBusy(key);
    const r: any = await setDepotMinimum({ providerId, depotId, minQuantityYds: min.trim() === "" ? null : Number(min) });
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setRows(r);
    toast.add(min.trim() === "" ? "Minimum removed." : "Minimum saved.", "var(--theme-ui-colors-success)");
    if (key === "new") setDraft({ providerId: "", depotId: "", min: "" });
  };

  return (
    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px", marginBottom: "10px" }}>
      <Box>
        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
          Provider &amp; depot minimums
        </H5>
        <Subtext marginTop={0} marginBottom={"10px"} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
          Applies to the service options this provider supplies. Below it, another provider takes the option if one can; otherwise the client sees it flagged with &ldquo;Use N yd&rdquo;. A depot minimum beats its provider&rsquo;s.
        </Subtext>
      </Box>
      <Box>
        <table style={{ width: "100%", borderCollapse: "collapse", maxWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Provider</th>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Depot</th>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600, width: 120 }}>Minimum (yd)</th>
              <th style={{ ...cell, width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <MinRow key={r.id} r={r} busy={busy} onSave={save} />
            ))}
            <tr>
              <td style={cell}>
                <select style={{ ...input, width: "100%" }} value={draft.providerId} onChange={(e) => setDraft({ providerId: e.target.value, depotId: "", min: draft.min })}>
                  <option value="">{"Add a provider\u2026"}</option>
                  {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td style={cell}>
                <select style={{ ...input, width: "100%" }} value={draft.depotId} disabled={!draft.providerId} onChange={(e) => setDraft({ ...draft, depotId: e.target.value })}>
                  <option value="">All depots (provider-wide)</option>
                  {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </td>
              <td style={cell}><input type="number" min={0} step={1} style={{ ...input, width: 100 }} value={draft.min} onChange={(e) => setDraft({ ...draft, min: e.target.value })} /></td>
              <td style={cell}>
                <BasicButton type="button" keepText disabled={!draft.providerId || draft.min.trim() === "" || busy === "new"} onClick={() => save(draft.providerId, draft.depotId || null, draft.min, "new")}>Add</BasicButton>
              </td>
            </tr>
          </tbody>
        </table>
        <Flex sx={{ marginTop: "6px" }}>
          <span style={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>Instant Estimate only. Staff-built quotes are not restricted.</span>
        </Flex>
      </Box>
    </Grid>
  );
};

const MinRow: React.FC<{ r: DepotMinimumRow; busy: string | null; onSave: (providerId: string, depotId: string | null, min: string, key: string) => void }> = ({ r, busy, onSave }) => {
  const [min, setMin] = useState(String(r.minQuantityYds ?? ""));
  return (
    <tr>
      <td style={cell}>{r.providerName}</td>
      <td style={cell}>{r.depotName ?? "All depots"}</td>
      <td style={cell}><input type="number" min={0} step={1} style={{ ...input, width: 100 }} value={min} onChange={(e) => setMin(e.target.value)} /></td>
      <td style={cell}>
        <Flex sx={{ gap: "6px" }}>
          <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === r.id} onClick={() => onSave(r.providerId, r.depotId, min, r.id)}>Save</BasicButton>
          <BasicButton outline scheme="danger" type="button" keepText disabled={busy === r.id} onClick={() => onSave(r.providerId, r.depotId, "", r.id)}>Remove</BasicButton>
        </Flex>
      </td>
    </tr>
  );
};

export default DepotMinimumsSetting;
